import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PURCHASES_ENABLED } from '../Constants';
import { productNames, productInfo, isProductLocked, REPEATABLE_PRODUCTS } from '../products';
import PromoCountdown, { promoDeadline } from './PromoCountdown';
import './styles/ProductCatalog.scss';

export type Pricing = {
    product: number;
    basePrice: number;
    discountedPrice: number | null;
    effectivePrice: number;
    paymentModel: number;
    firstPaymentAmount: number;
    firstPartAmount: number | null;
    secondPartDueDays: number | null;
    monthCount: number | null;
    // Global promo deadline, present only while this product's launch price is live.
    discountValidUntil: string | null;
    // Admin sale switch — when false the product shows "Nyní nelze zakoupit" and can't be bought.
    available: boolean;
};

// Re-exported so the many call sites that already import product names from here keep working.
export { productNames } from '../products';

export function formatPrice(value: number) {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") + " Kč";
}

export function paymentModelLabel(p: Pricing) {
    if (p.paymentModel === 1) return "ve dvou platbách";
    if (p.paymentModel === 2) return "měsíčně × " + (p.monthCount ?? 0);
    return "jednorázově";
}

// Shared product catalogue grid used both on the public sale page and in the
// account's "Dokup / upgrade" section. Owned products are shown disabled
// (except repeatable ones, which stay buyable). Selecting a plan opens the
// dedicated checkout, which handles both logged-in and guest purchases.
export default function ProductCatalog({ pricing, ownedProducts = [], buyLabel = "Vybrat plán", highlightProduct }:
    { pricing: Pricing[]; ownedProducts?: number[]; buyLabel?: string; highlightProduct?: number }) {
    const navigate = useNavigate();
    const highlightRef = useRef<HTMLDivElement | null>(null);

    // When arriving from a plan link (e.g. /nakup?plan=1), scroll the chosen
    // card into view so the user's intent carries straight into the catalogue.
    useEffect(() => {
        if (highlightProduct != null) {
            highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [highlightProduct, pricing]);

    const deadline = promoDeadline(pricing);

    return (
        <div className="productCatalog">
            {deadline &&
                <div className="catalogPromo">
                    <PromoCountdown deadline={deadline} />
                </div>
            }
            <div className="grid">
                {pricing.map((p) => {
                    const info = productInfo[p.product] ?? { tagline: "", desc: "", features: [] };
                    const isInstallment = p.paymentModel !== 0;
                    const repeatable = REPEATABLE_PRODUCTS.includes(p.product);
                    const alreadyOwned = ownedProducts.includes(p.product);
                    // Locked = owned outright, or already covered by a product the buyer owns
                    // (e.g. the course includes the materials). Extra hours stay buyable.
                    const locked = isProductLocked(p.product, ownedProducts);
                    const highlighted = p.product === highlightProduct;

                    return (
                        <div key={p.product}
                            ref={highlighted ? highlightRef : undefined}
                            className={"productCard" + (info.recommended && !locked ? " featured" : "") + (locked ? " owned" : "") + (highlighted ? " highlight" : "")}>
                            {info.recommended && !locked && <span className="ribbon">NEJOBLÍBENĚJŠÍ</span>}

                            <span className="badge">{paymentModelLabel(p)}</span>
                            <h2>{productNames[p.product]}</h2>
                            <span className="tagline">{info.tagline}</span>

                            <div className="priceLine">
                                {p.discountedPrice != null &&
                                    <span className="prev">{formatPrice(p.basePrice)}</span>
                                }
                                <span className="now">{formatPrice(p.effectivePrice)}</span>
                                {p.paymentModel === 2 && <span className="per">/ měsíc</span>}
                            </div>

                            {isInstallment
                                ? <span className="first">První platba {formatPrice(p.firstPaymentAmount)}</span>
                                : <span className="first muted">Jednorázová platba</span>
                            }

                            <p className="desc">{info.desc}</p>

                            <ul className="features">
                                {info.features.map((f, i) => <li key={i}>{f}</li>)}
                            </ul>

                            {locked
                                ? <div className="ownedTag">{alreadyOwned ? "✓ Již máš zakoupeno" : "✓ Už k tomu máš přístup"}</div>
                                : !PURCHASES_ENABLED
                                    ? <div className="soon">Brzy v prodeji</div>
                                    : !p.available
                                        ? <div className="notForSale">Nyní nelze zakoupit</div>
                                        : <button type="button" className="buy" onClick={() => navigate("/pokladna/" + p.product)}>
                                            {alreadyOwned && repeatable ? "Koupit další" : buyLabel}
                                        </button>
                            }
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
